import {
  Box,
  IconButton,
  lighten,
  makeStyles,
  SvgIcon,
  Tooltip,
  withStyles,
} from '@material-ui/core';
import { freshdeskLogo, zendeskLogo } from '@src/resources';
import MetaIcon from '@src/resources/icons/MetaIcon';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  CloseIcon,
  CopyIcon,
  FailureBadgeIcon,
  SuccessBadgeIcon,
  TagsIcon,
  TerminalIcon,
} from './SvgIcons';

// eslint-disable-next-line no-shadow
export const enum ActionButtonsType {
  tags = 'tags',
  capi = 'capi',
  capiLead = 'capi/LEAD',
  capiPurchase = 'capi/PURCHASE',
  crm = 'crm',
  ticket = 'ticket',
  ticketFreshdesk = 'ticketFreshdesk',
  close = 'close',
}

export interface IActionButtons {
  onClick: React.MouseEventHandler<HTMLElement>;
  type: ActionButtonsType;
  disabled?: boolean;
  status?: string;
  ticketId?: string;
}

const StyledTooltip = withStyles(() => ({
  tooltip: {
    fontFamily: 'Poppins',
    fontSize: 12,
    textTransform: 'capitalize',
  },
}))(Tooltip);

const icons = {
  tags: TagsIcon,
  [ActionButtonsType.capi]: <MetaIcon />,
  [ActionButtonsType.capiLead]: <MetaIcon />,
  [ActionButtonsType.capiPurchase]: <MetaIcon />,
  crm: TerminalIcon,
  ticket: (
    <img
      src={zendeskLogo}
      style={{ width: 16 }}
      alt="zendesk"
      className="providerLogo"
    />
  ),
  ticketFreshdesk: (
    <img
      style={{ width: 18.5 }}
      src={freshdeskLogo}
      alt="freshdesk"
      className="providerLogo"
    />
  ),
  close: CloseIcon,
};

const tooltipText = {
  tags: 'AgentToolbar.actionButton.contactTags.tooltip',
  [ActionButtonsType.capi]: 'AgentToolbar.actionButton.CAPI.tooltip',
  [ActionButtonsType.capiLead]: 'AgentToolbar.actionButton.CAPI.LEAD.tooltip',
  [ActionButtonsType.capiPurchase]:
    'AgentToolbar.actionButton.CAPI.PURCHASE.tooltip',
  crm: 'AgentToolbar.actionButton.CRM.tooltip',
  ticket: 'AgentToolbar.actionButton.ticket.tooltip',
  ticketFreshdesk: 'AgentToolbar.actionButton.ticket.tooltip',
  close: 'AgentToolbar.actionButton.close.tooltip',
};

const ActionButtons: React.FC<IActionButtons> = ({
  onClick,
  type,
  disabled = false,
  status = '',
  ticketId = '',
}) => {
  const { formatMessage } = useIntl();

  const [active, setActive] = useState(false);
  const [open, setOpen] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const {
    root,
    tags,
    capi,
    capiLead,
    capiPurchase,
    tagsActive,
    crm,
    ticket,
    close,
    tagsTooltip,
    capiTooltip,
    capiLeadTooltip,
    capiPurchaseTooltip,
    closeTooltip,
    ticketTooltip,
    crmTooltip,
  } = useStyles({ active, type });

  const buttonClasses = {
    [ActionButtonsType.tags]: tags,
    [ActionButtonsType.capi]: capi,
    [ActionButtonsType.capiLead]: capiLead,
    [ActionButtonsType.capiPurchase]: capiPurchase,
    [ActionButtonsType.crm]: crm,
    [ActionButtonsType.ticket]: ticket,
    [ActionButtonsType.ticketFreshdesk]: ticket,
    [ActionButtonsType.close]: close,
  };
  const buttonClass = buttonClasses[type] || '';

  const activeClass =
    active && type === ActionButtonsType.tags ? tagsActive : '';

  const tooltipClasses = {
    [ActionButtonsType.tags]: tagsTooltip,
    [ActionButtonsType.capi]: capiTooltip,
    [ActionButtonsType.capiLead]: capiLeadTooltip,
    [ActionButtonsType.capiPurchase]: capiPurchaseTooltip,
    [ActionButtonsType.ticket]: ticketTooltip,
    [ActionButtonsType.ticketFreshdesk]: ticketTooltip,
    [ActionButtonsType.close]: closeTooltip,
    [ActionButtonsType.crm]: crmTooltip,
  };

  const tooltipClass = tooltipClasses[type] || '';

  const title =
    ActionButtonsType.ticket && disabled && ticketId ? (
      <Box
        display="flex"
        alignItems="center"
        style={{ gap: '6px', cursor: 'pointer' }}
        onClick={() => {
          navigator?.clipboard?.writeText(ticketId);
          setOpen(false);
          setKeepOpen(false);
        }}
      >
        Ticket: #{ticketId}
        <SvgIcon fontSize="small">{CopyIcon}</SvgIcon>
      </Box>
    ) : (
      formatMessage({ id: tooltipText[type] })
    );

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    if (type === ActionButtonsType.ticket && ticketId) {
      setKeepOpen(true);
    }
    setOpen(true);
  };

  return (
    <StyledTooltip
      title={title}
      placement="top-end"
      classes={{ tooltip: tooltipClass }}
      arrow
      open={open || keepOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      interactive
      PopperProps={{
        style: {
          marginTop: -9,
          marginBottom: -9,
        },
      }}
    >
      <Box position="relative">
        <IconButton
          className={`${root} ${buttonClass} ${activeClass}`}
          data-testid={`action-button-${type}`}
          onClick={event => {
            onClick(event);
            setActive(!active);
          }}
          disabled={disabled}
        >
          {type === ActionButtonsType.ticket ||
          type === ActionButtonsType.ticketFreshdesk ||
          type.startsWith('capi') ? (
            icons[type]
          ) : (
            <SvgIcon fontSize="small" color="inherit">
              {icons[type]}
            </SvgIcon>
          )}
        </IconButton>
        {status && (
          <Box position="absolute" top="0px" right="-3px">
            {status === 'success' && SuccessBadgeIcon}
            {status === 'failure' && FailureBadgeIcon}
          </Box>
        )}
      </Box>
    </StyledTooltip>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: '8px',
    width: '36px',
    height: '36px',
    borderRadius: '24px',
  },
  tags: {
    border: `1px solid ${theme.b2chatyellow[500]}`,
    color: theme.b2chatyellow[500],
    '&:hover': {
      backgroundColor: theme.b2chatyellow[300],
    },
  },
  tagsActive: {
    backgroundColor: theme.b2chatyellow[300],
  },
  tagsTooltip: {
    border: `0.5px solid ${theme.b2chatyellow[500]}`,
    color: theme.b2chatyellow[500],
    padding: '5px 10px',
    backgroundColor: theme.b2chatyellow[300],
    [`& .MuiTooltip-arrow`]: {
      '&:before': {
        border: `0.5px solid ${theme.b2chatyellow[500]}`,
      },
      color: theme.b2chatyellow[300],
    },
  },
  capi: {
    border: `1px solid currentColor`,
    color: `${theme.capicolor.green}`,
    '&:hover': {
      backgroundColor: lighten(theme.capicolor.green, 0.9),
    },
  },
  capiTooltip: {
    padding: '5px 10px',
    border: `0.5px solid currentColor`,
    color: theme.capicolor.green,
    backgroundColor: lighten(theme.capicolor.green, 0.9),
    [`& .MuiTooltip-arrow`]: {
      '&:before': {
        border: `0.5px solid ${theme.capicolor.green}`,
      },
      color: lighten(theme.capicolor.green, 0.9),
    },
  },
  capiPurchase: {
    border: `1px solid currentColor`,
    color: `${theme.capicolor.blue}`,
    '&:hover': {
      backgroundColor: lighten(theme.capicolor.blue, 0.9),
    },
  },
  capiPurchaseTooltip: {
    padding: '5px 10px',
    color: theme.capicolor.blue,
    border: `0.5px solid currentColor`,
    backgroundColor: lighten(theme.capicolor.blue, 0.9),
    [`& .MuiTooltip-arrow`]: {
      '&:before': {
        border: `0.5px solid ${theme.capicolor.blue}`,
      },
      color: lighten(theme.capicolor.blue, 0.9),
    },
  },
  capiLead: {
    border: `1px solid currentColor`,
    color: `${theme.capicolor.orange}`,
    '&:hover': {
      backgroundColor: lighten(theme.capicolor.orange, 0.9),
    },
  },
  capiLeadTooltip: {
    padding: '5px 10px',
    border: `0.5px solid currentColor`,
    color: theme.capicolor.orange,
    backgroundColor: lighten(theme.capicolor.orange, 0.9),
    [`& .MuiTooltip-arrow`]: {
      '&:before': {
        border: `0.5px solid ${theme.capicolor.orange}`,
      },
      color: lighten(theme.capicolor.orange, 0.9),
    },
  },
  crm: {
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    '&:hover': {
      Opacity: 0.5,
    },
    '&:disabled': {
      border: `1px solid ${theme.palette.action.disabledBackground}`,
    },
  },
  crmTooltip: {
    border: `0.5px solid ${theme.palette.primary.main}`,
    padding: '5px 10px',
    color: theme.palette.primary.main,
    backgroundColor: theme.b2chatblue[200],
    [`& .MuiTooltip-arrow`]: {
      '&:before': {
        border: `0.5px solid ${theme.palette.primary.main}`,
      },
      color: theme.b2chatblue[200],
    },
  },

  ticket: {
    border: `1px solid ${theme.b2chatgreen[500]}`,
    color: theme.b2chatgreen[500],
    '&:hover': {
      opacity: 0.5,
    },
    '&:disabled': {
      border: `1px solid ${theme.palette.action.disabledBackground}`,
      color: theme.palette.action.disabled,
      filter: 'grayscale(1)',
    },
  },

  ticketTooltip: {
    border: `0.5px solid ${theme.b2chatgreen[500]}`,
    padding: '5px 10px',
    color: theme.b2chatgreen[500],
    backgroundColor: theme.b2chatgreen[50],
    [`& .MuiTooltip-arrow`]: {
      '&:before': {
        border: `0.5px solid ${theme.b2chatgreen[500]}`,
      },
      color: theme.b2chatgreen[50],
    },
  },

  close: {
    border: `1px solid ${theme.b2chatred[500]}`,
    color: theme.b2chatred[500],
    '&:hover': {
      Opacity: 0.5,
    },
    '&:disabled': {
      border: `1px solid ${theme.palette.action.disabledBackground}`,
      backgroundColor: theme.palette.action.disabledBackground,
      opacity: 0.5,
    },
  },

  closeTooltip: {
    border: `0.5px solid ${theme.b2chatred[500]}`,
    color: theme.b2chatred[500],
    padding: '5px 10px',
    backgroundColor: theme.b2chatred[50],
    [`& .MuiTooltip-arrow`]: {
      '&:before': {
        border: `0.5px solid ${theme.b2chatred[500]}`,
      },
      color: theme.b2chatred[50],
    },
  },
}));

export default ActionButtons;
